/**
 * Dadware product spotlight — dw-product.js (deferred; uses dw-commerce.js).
 *
 * <dw-spotlight> owns one purchase UI: gallery, options, multi-buy sets,
 * mix-and-match slots, add to cart, express checkout visibility and the
 * phone sticky bar. Set totals come from DW.setTotals and the product's
 * Multi-buy metafields; matching Shopify automatic discounts charge the same.
 * Standard events: product views come from <product-component>, add to cart
 * from DW.addToCart; this file adds ProductSelectEvent and custom
 * dadware:* events only.
 */
(() => {
  'use strict';
  if (customElements.get('dw-spotlight')) return;

  const qsa = (root, selector) => Array.from(root.querySelectorAll(selector));
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fill = (template, vars) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in vars ? String(vars[key]) : match));

  class DwSpotlight extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const json = this.querySelector('script[data-dw-json]');
      if (!json) return;
      try {
        this.data = JSON.parse(json.textContent);
      } catch (error) {
        return;
      }
      this.dataset.ready = 'true';
      this.sid = this.dataset.sectionId;
      this.product = this.data.product;
      this.strings = this.data.strings || {};
      this.designs = [this.product].concat(this.data.mixProducts || []);
      this.form = this.querySelector('[data-dw-form]');
      this.mode = 'same';
      this.slots = [];
      this.observers = [];
      this.els = {
        id: this.querySelector('[data-dw-id]'),
        qty: this.querySelector('[data-dw-qty]'),
        opts: this.querySelector('[data-dw-opts]'),
        mode: this.querySelector('[data-dw-mode]'),
        slots: this.querySelector('[data-dw-slots]'),
        buy: this.querySelector('[data-dw-buy]'),
        atc: this.querySelector('[data-dw-atc]'),
        atcLabel: this.querySelector('[data-dw-atc-label]'),
        atcPrice: this.querySelector('[data-dw-atc-price]'),
        error: this.querySelector('[data-dw-error]'),
        express: this.querySelector('[data-dw-express]'),
        expressNote: this.querySelector('[data-dw-express-note]'),
        priceCurrent: this.querySelector('[data-dw-price-current]'),
        priceCompare: this.querySelector('[data-dw-price-compare]'),
        live: this.querySelector('[data-dw-live]'),
        sticky: this.querySelector('[data-dw-sticky]'),
      };
      this.tierInputs = qsa(this, '.dw-offer__input');

      this.bindOptions();
      this.bindOffers();
      this.bindMode();
      this.bindForm();
      this.initGallery();
      this.initDialogs();
      this.initSticky();
      this.applySetParam();
      this.update({ silent: true });
    }

    // ?set=2 on a product link (for example from an ad) preselects that set.
    // It only picks a set; nothing is added to the cart.
    applySetParam() {
      if (!this.tierInputs.length || document.querySelector('[data-dw-set-applied]')) return;
      const wanted = parseInt(new URLSearchParams(window.location.search).get('set'), 10);
      if (!wanted) return;
      const input = this.tierInputs.find((i) => parseInt(i.value, 10) === wanted);
      if (!input) return;
      this.setAttribute('data-dw-set-applied', '');
      this.selectTier(input);
    }

    selectTier(input) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    disconnectedCallback() {
      this.observers.forEach((observer) => observer.disconnect());
      this.observers = [];
      if (this.hasAttribute('data-dw-sticky-owner')) document.documentElement.classList.remove('dw-sticky-on');
    }

    /* ----------------------------------------------------------- helpers */
    get DW() {
      return window.DW || null;
    }

    money(cents) {
      return this.DW && this.DW.formatMoney ? this.DW.formatMoney(cents) : (Number(cents || 0) / 100).toFixed(2);
    }

    basePrice(product) {
      const variant = product.variants.find((v) => v.available) || product.variants[0];
      return variant ? variant.price : 0;
    }

    selectedOptions(container = this.els.opts) {
      if (!container) return [];
      // The picker is <select> per option now, not a radio pill group.
      return qsa(container, 'select[data-dw-option]').map((sel) => (sel.value ? sel.value : null));
    }

    findVariant(product, values) {
      if (!product || !product.variants.length) return null;
      if (product.onlyDefault) return product.variants[0];
      if (!values.length || values.some((value) => value == null)) return null;
      return product.variants.find((v) => v.options.every((option, i) => option === values[i])) || null;
    }

    markAvailability(container, product, values) {
      qsa(container, 'select[data-dw-option]').forEach((sel) => {
        const index = Number(sel.dataset.dwOption);
        Array.from(sel.options).forEach((opt) => {
          if (!opt.value) return; // the placeholder row
          const possible = product.variants.some(
            (v) =>
              v.available &&
              v.options[index] === opt.value &&
              v.options.every((option, j) => j === index || values[j] == null || values[j] === option)
          );
          // Flag rather than remove, so the shopper can still see the option exists.
          opt.toggleAttribute('data-unavailable', !possible);
        });
      });
    }

    tierQty() {
      const checked = this.tierInputs.find((input) => input.checked);
      return checked ? Math.max(1, parseInt(checked.value, 10) || 1) : 1;
    }

    totalsFor(prices) {
      const DW = this.DW;
      const bundle = this.data.offersOn ? this.product.bundle : null;
      if (DW && DW.setTotals) return DW.setTotals(prices, bundle);
      const subtotal = prices.reduce((sum, price) => sum + price, 0);
      return { subtotal, discount: 0, total: subtotal, count: prices.length, each: prices.length ? Math.round(subtotal / prices.length) : 0 };
    }

    currentPrices() {
      if (this.mode === 'mix') return this.slots.map((slot) => (slot.variant ? slot.variant.price : this.basePrice(slot.product)));
      const unit = this.currentVariant ? this.currentVariant.price : this.basePrice(this.product);
      return Array.from({ length: this.tierQty() }, () => unit);
    }

    showError(message) {
      if (!this.els.error) return;
      this.els.error.textContent = message;
      this.els.error.hidden = false;
    }

    hideError() {
      if (!this.els.error) return;
      this.els.error.hidden = true;
      this.els.error.textContent = '';
    }

    /* ------------------------------------------------------------ inputs */
    bindOptions() {
      if (!this.els.opts) return;
      this.els.opts.addEventListener('change', (event) => {
        const group = event.target.matches('select[data-dw-option]') ? event.target : null;
        if (!group) return;
        group.classList.remove('is-invalid');
        this.update();
        this.dispatchSelect();
        if (this.currentVariant && this.currentVariant.media) this.goToMedia(this.currentVariant.media);
        if (this.DW) this.DW.track('variant_selected', { productId: this.product.id, variantId: this.currentVariant ? this.currentVariant.id : null });
      });
    }

    bindOffers() {
      this.tierInputs.forEach((input) =>
        input.addEventListener('change', () => {
          const qty = this.tierQty();
          if (this.els.mode) {
            this.els.mode.hidden = qty < 2;
            if (qty < 2 && this.mode === 'mix') {
              this.mode = 'same';
              const same = this.els.mode.querySelector('input[value="same"]');
              if (same) same.checked = true;
            }
          }
          this.syncSlots();
          this.update();
          if (this.DW) this.DW.track('bundle_selected', { productId: this.product.id, quantity: qty, tier: Number(input.dataset.tier), mode: this.mode });
        })
      );
    }

    // Retained for older markup that still ships the same/mix toggle.
    bindMode() {
      if (!this.els.mode) return;
      if (!this.els.mode) return;
      this.els.mode.addEventListener('change', (event) => {
        this.mode = event.target.value === 'mix' ? 'mix' : 'same';
        this.syncSlots();
        this.update();
      });
    }

    /* ------------------------------------------------------ mix and match */
    syncSlots() {
      const box = this.els.slots;
      if (!box) return;
      // Per-item rows appear as soon as a multi-item set is chosen. There is no
      // mode toggle: choosing "2 pairs" should just show two rows to fill in.
      const qty = this.tierQty();
      const on = qty > 1;
      this.mode = on ? 'mix' : 'same';
      box.hidden = !on;
      if (this.els.opts) this.els.opts.hidden = on;
      const label = box.querySelector('[data-dw-slots-label]');
      if (label && !label.hasAttribute('data-dw-label-fixed')) {
        // No merchant-supplied label — fall back to the option names.
        const names = this.product.onlyDefault ? [] : this.product.options;
        label.textContent = names.length ? names.join(', ') : '';
        label.hidden = !names.length;
      } else if (label) {
        label.hidden = !label.textContent.trim();
      }
      if (!on) return;
      while (this.slots.length < qty) {
        const first = this.slots.length === 0;
        const values = first ? this.selectedOptions() : this.product.options.map(() => null);
        this.slots.push({ product: this.product, values, variant: null });
      }
      this.slots.length = qty;
      const list = box.querySelector('[data-dw-slots-list]') || box;
      list.replaceChildren(...this.slots.map((slot, index) => this.renderSlot(slot, index)));
    }

    renderSlot(slot, index) {
      /*
        Compact slot row: thumbnail + one <select> per option (+ a design
        select when the set allows mixing products).

        Selects rather than pill radios. With three items and two options each,
        pills run to eighteen targets and push Add to cart off the screen;
        selects keep every item on one line, so all three set options plus the
        chosen item's rows stay visible together on a phone.
      */
      const wrap = document.createElement('fieldset');
      wrap.className = 'dw-slot';
      wrap.dataset.slot = String(index);

      const legend = document.createElement('legend');
      legend.className = 'visually-hidden';
      legend.textContent = fill(this.strings.item, { number: index + 1 });
      wrap.appendChild(legend);

      const row = document.createElement('div');
      row.className = 'dw-slot__row';

      // Thumbnail — identifies the item at a glance when designs differ.
      const figure = document.createElement('span');
      figure.className = 'dw-slot__thumb';
      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 48;
      img.height = 48;
      // Show the photo for the variant this row has chosen, so three different
      // colours give three different previews. Falls back to the product shot
      // until enough options are picked to resolve a variant.
      const setThumb = () => {
        const map = slot.product.mediaMap || {};
        const mediaId = slot.variant && slot.variant.media;
        const src = (mediaId && map[mediaId]) || slot.product.image || '';
        if (src) {
          img.src = src;
          img.alt = slot.variant ? slot.variant.title : '';
          figure.hidden = false;
        } else {
          figure.hidden = true;
        }
      };
      setThumb();
      figure.appendChild(img);
      row.appendChild(figure);

      const selects = document.createElement('span');
      selects.className = 'dw-slot__selects';
      row.appendChild(selects);

      // Design select, only when this product has siblings to swap between.
      if (this.designs.length > 1) {
        const id = `Slot-${this.sid}-${index}-design`;
        const select = document.createElement('select');
        select.id = id;
        select.className = 'dw-select dw-slot__select';
        select.setAttribute('aria-label', this.strings.design);
        this.designs.forEach((design) => {
          const option = document.createElement('option');
          option.value = String(design.id);
          option.textContent = design.title;
          option.selected = design.id === slot.product.id;
          select.appendChild(option);
        });
        select.addEventListener('change', () => {
          const next = this.designs.find((design) => String(design.id) === select.value);
          if (!next) return;
          slot.product = next;
          slot.values = next.onlyDefault ? [] : next.options.map(() => null);
          slot.variant = null;
          const fresh = this.renderSlot(slot, index);
          wrap.replaceWith(fresh);
          fresh.querySelector('select')?.focus();
          this.update();
        });
        selects.appendChild(select);
      }

      if (slot.product.onlyDefault) {
        slot.variant = slot.product.variants[0] || null;
        wrap.appendChild(row);
        return wrap;
      }

      slot.product.options.forEach((name, i) => {
        const values = [...new Set(slot.product.variants.map((v) => v.options[i]))];
        if (values.length === 1) slot.values[i] = values[0];

        const select = document.createElement('select');
        select.className = 'dw-select dw-slot__select';
        select.dataset.dwOption = String(i);
        select.setAttribute('aria-label', `${name} — ${fill(this.strings.item, { number: index + 1 })}`);

        if (slot.values[i] === null || slot.values[i] === undefined) {
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = name;
          placeholder.disabled = true;
          placeholder.selected = true;
          select.appendChild(placeholder);
        }

        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.selected = slot.values[i] === value;
          // Mark combinations this product cannot make, rather than hiding them.
          const probe = slot.values.slice();
          probe[i] = value;
          const match = this.findVariant(slot.product, probe);
          if (probe.every((v) => v !== null && v !== undefined) && match && !match.available) {
            option.textContent = `${value} — ${this.strings.unavailable}`;
            option.disabled = true;
          }
          select.appendChild(option);
        });

        select.addEventListener('change', () => {
          slot.values[i] = select.value;
          slot.variant = this.findVariant(slot.product, slot.values);
          setThumb();
          this.markAvailability(wrap, slot.product, slot.values);
          wrap.classList.remove('is-invalid');
          // Move the main gallery to the row just touched, so the big image
          // confirms the choice rather than staying on an unrelated colour.
          if (slot.variant && slot.variant.media) this.goToMedia(slot.variant.media);
          this.update();
        });

        selects.appendChild(select);
      });

      wrap.appendChild(row);
      slot.variant = this.findVariant(slot.product, slot.values);
      setThumb();
      this.markAvailability(wrap, slot.product, slot.values);
      return wrap;
    }

    /* ------------------------------------------------------------- state */
    update(options = {}) {
      const mix = this.mode === 'mix';
      const product = this.product;
      const values = this.selectedOptions();
      this.currentVariant = this.findVariant(product, values);
      if (this.els.opts) this.markAvailability(this.els.opts, product, values);

      const variant = mix ? null : this.currentVariant;
      const prices = this.currentPrices();
      const totals = this.totalsFor(prices);
      const count = prices.length;

      const unit = variant ? variant.price : this.basePrice(product);
      const compare = variant ? variant.compare : 0;
      if (this.els.priceCurrent) this.els.priceCurrent.textContent = this.money(unit);
      if (this.els.priceCompare) {
        this.els.priceCompare.hidden = !(compare > unit);
        if (compare > unit) this.els.priceCompare.textContent = this.money(compare);
      }

      const allChosen = !values.length || values.every((value) => value != null);
      const complete = mix ? this.slots.length > 0 && this.slots.every((slot) => slot.variant) : Boolean(variant);
      const unavailable = !mix && allChosen && !variant && !product.onlyDefault;
      const soldOut = !product.available || (mix ? this.slots.some((slot) => slot.variant && !slot.variant.available) : Boolean(variant && !variant.available));

      if (this.els.id) {
        this.els.id.disabled = !variant;
        if (variant) this.els.id.value = variant.id;
      }
      if (this.els.qty) this.els.qty.value = String(mix ? 1 : this.tierQty());

      let label = count > 1 ? fill(this.strings.addCount, { count }) : this.strings.addToCart;
      if (soldOut) label = this.strings.soldOut;
      else if (unavailable) label = this.strings.unavailable;
      if (this.els.atcLabel && !this.busy) this.els.atcLabel.textContent = label;
      if (this.els.atc && !this.busy) this.els.atc.disabled = soldOut || unavailable;
      if (this.els.atcPrice) {
        this.els.atcPrice.hidden = soldOut || unavailable;
        this.els.atcPrice.textContent = this.money(totals.total);
      }

      if (this.els.express) {
        const expressOk = !mix && Boolean(variant) && variant.available;
        this.els.express.classList.toggle('is-collapsed', !expressOk);
        if (this.els.expressNote) {
          this.els.expressNote.hidden = expressOk || soldOut;
          this.els.expressNote.textContent = mix ? this.strings.expressMix : this.strings.expressSelect;
        }
      }

      this.renderOffers();
      this.updateSticky(totals, complete, soldOut || unavailable);

      if (!options.silent) {
        this.hideError();
        if (this.els.live && complete) {
          const selection = mix ? fill(this.strings.addCount, { count }) : variant.title;
          this.els.live.textContent = fill(this.strings.selected, { selection, price: this.money(totals.total) });
        }
        if (this.data.pdp && variant && window.history && typeof history.replaceState === 'function') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState(history.state, '', url.toString());
        }
      }
    }

    renderOffers() {
      if (!this.tierInputs.length) return;
      const unit = this.currentVariant ? this.currentVariant.price : this.basePrice(this.product);
      this.tierInputs.forEach((input) => {
        const qty = Math.max(1, parseInt(input.value, 10) || 1);
        const prices = this.mode === 'mix' && input.checked ? this.currentPrices() : Array.from({ length: qty }, () => unit);
        const totals = this.totalsFor(prices);
        const card = input.nextElementSibling;
        if (!card) return;
        const total = card.querySelector('[data-dw-offer-total]');
        const compare = card.querySelector('[data-dw-offer-compare]');
        const save = card.querySelector('[data-dw-offer-save]');
        const each = card.querySelector('[data-dw-offer-each]');
        const pct = card.querySelector('[data-dw-offer-pct]');
        if (total) total.textContent = this.money(totals.total);
        if (each) {
          // Per-unit price. Shown from two units up, where it is the number
          // that makes the set legible as a better deal.
          each.hidden = qty < 2;
          each.textContent = fill(this.strings.each, { price: this.money(totals.each) });
        }
        if (compare) {
          compare.hidden = !(totals.discount > 0);
          compare.textContent = this.money(totals.subtotal);
        }
        if (save) {
          save.hidden = !(totals.discount > 0);
          save.textContent = fill(this.strings.save, { amount: this.money(totals.discount) });
        }
        if (pct) {
          const percent = totals.subtotal > 0 ? Math.round((totals.discount * 100) / totals.subtotal) : 0;
          pct.hidden = percent <= 0;
          pct.textContent = fill(this.strings.youSavePercent, { percent });
        }
      });
    }

    dispatchSelect() {
      const SE = window.StandardEvents;
      if (!SE || !SE.ProductSelectEvent || this.data.demo) return;
      const values = this.selectedOptions();
      const selectedOptions = this.product.onlyDefault
        ? []
        : this.product.options.map((name, i) => ({ name, value: values[i] })).filter((option) => option.value != null);
      const deferred = SE.ProductSelectEvent.createPromise();
      this.dispatchEvent(
        new SE.ProductSelectEvent({
          product: { id: String(this.product.id), title: this.product.title, handle: this.product.handle },
          selectedOptions,
          promise: deferred.promise,
        })
      );
      const v = this.currentVariant;
      deferred.resolve(
        v
          ? {
              variant: {
                id: v.id,
                title: v.title,
                availableForSale: v.available,
                price: { amount: (v.price / 100).toFixed(2), currencyCode: this.data.currency },
                selectedOptions,
              },
            }
          : { variant: null }
      );
    }

    /* -------------------------------------------------------------- cart */
    bindForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.addToCart();
      });
    }

    focusMissing() {
      if (this.mode === 'mix') {
        const missing = this.slots.findIndex((slot) => !slot.variant);
        if (missing < 0) return false;
        const el = this.els.slots.querySelector(`[data-slot="${missing}"]`);
        if (el) {
          el.classList.add('is-invalid');
          el.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
          el.querySelector('select, input')?.focus({ preventScroll: true });
        }
        this.showError(fill(this.strings.missingSlot, { number: missing + 1 }));
        return true;
      }
      const values = this.selectedOptions();
      const missing = values.findIndex((value) => value == null);
      if (missing < 0) return false;
      // [data-dw-option] is the <select> itself now, so focus it directly.
      const group = this.els.opts.querySelector(`[data-dw-option="${missing}"]`);
      if (group) {
        group.classList.add('is-invalid');
        group.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' });
        group.focus({ preventScroll: true });
      }
      this.showError(fill(this.strings.missingOption, { option: String(this.product.options[missing] || '').toLowerCase() }));
      return true;
    }

    async addToCart() {
      if (this.busy) return;
      if (this.data.demo) {
        if (!this.focusMissing()) this.showError(this.strings.demoCart);
        return;
      }
      if (this.focusMissing()) return;
      const mix = this.mode === 'mix';
      if (!mix && !this.currentVariant) {
        this.showError(this.strings.unavailable);
        return;
      }
      const items = mix
        ? this.slots.map((slot) => ({ id: slot.variant.id, quantity: 1 }))
        : [{ id: this.currentVariant.id, quantity: this.tierQty() }];

      const DW = this.DW;
      if (!DW || typeof DW.addToCart !== 'function') {
        if (!mix && this.form) this.form.submit();
        return;
      }

      const button = this.els.atc;
      this.busy = true;
      this.hideError();
      button.disabled = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      this.els.atcLabel.textContent = this.strings.adding;
      const units = items.reduce((sum, item) => sum + item.quantity, 0);

      try {
        await DW.addToCart(items, { source: 'dw-spotlight', eventTarget: button });
        if (units > 1) DW.track('set_added', { productId: this.product.id, units, mode: this.mode });
        button.classList.add('is-success');
        this.els.atcLabel.textContent = this.strings.added;
        await new Promise((resolve) => setTimeout(resolve, 1400));
      } catch (error) {
        this.showError((error && error.message) || this.strings.errorGeneric);
      } finally {
        this.busy = false;
        button.classList.remove('is-loading', 'is-success');
        button.removeAttribute('aria-busy');
        this.update({ silent: true });
      }
    }

    /* ----------------------------------------------------------- gallery */
    initGallery() {
      const gallery = this.querySelector('[data-dw-gallery]');
      if (!gallery) return;
      const track = gallery.querySelector('[data-dw-track]');
      if (!track) return;
      const slides = qsa(track, '.dw-gallery__slide');
      const prev = gallery.querySelector('[data-dw-prev]');
      const next = gallery.querySelector('[data-dw-next]');
      const counter = gallery.querySelector('[data-dw-count]');
      const thumbs = qsa(gallery, '[data-dw-thumb]');
      const indexNow = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const go = (i) => {
        const target = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: target * track.clientWidth, behavior: reduced() ? 'auto' : 'smooth' });
      };
      this.gallery = { slides, go };

      prev?.addEventListener('click', () => go(indexNow() - 1));
      next?.addEventListener('click', () => go(indexNow() + 1));
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => go(Number(thumb.dataset.dwThumb))));
      track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          go(indexNow() + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          go(indexNow() - 1);
        }
      });

      let frame = 0;
      let last = -1;
      const sync = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const i = indexNow();
          if (i === last) return;
          last = i;
          if (counter) counter.textContent = String(i + 1);
          if (prev) prev.disabled = i <= 0;
          if (next) next.disabled = i >= slides.length - 1;
          thumbs.forEach((thumb, ti) => {
            thumb.classList.toggle('is-active', ti === i);
            if (ti === i) thumb.setAttribute('aria-current', 'true');
            else thumb.removeAttribute('aria-current');
          });
          slides.forEach((slide, si) => {
            const vids = slide.querySelectorAll('video');
            if (si !== i) {
              // Off-screen slides stop and rewind, so returning to one starts
              // from the top rather than mid-clip.
              vids.forEach((video) => {
                if (!video.paused) video.pause();
                if (video.matches('.dw-gallery__vid')) { try { video.currentTime = 0; } catch (e) {} }
              });
              return;
            }
            // The slide in view plays on its own — muted, so browsers allow it.
            if (reduced() || (navigator.connection && navigator.connection.saveData)) return;
            vids.forEach((video) => {
              if (!video.matches('.dw-gallery__vid')) return;
              if (video.dataset.userPaused) return;
              if (video.preload === 'none') video.preload = 'metadata';
              video.play().catch(() => {});
            });
          });
        });
      };
      track.addEventListener('scroll', sync, { passive: true });
      sync();

      slides.forEach((slide) => {
        slide.querySelectorAll('video.dw-gallery__vid').forEach((video) => {
          video.addEventListener('pause', () => {
            // Distinguish "visitor pressed pause" from our own slide-change pause.
            if (!video.ended && document.activeElement && slide.contains(document.activeElement)) {
              video.dataset.userPaused = 'true';
            }
          });
          video.addEventListener('play', () => { delete video.dataset.userPaused; });
        });
      });

      gallery.addEventListener('click', (event) => {
        const play = event.target.closest('[data-dw-external-play]');
        if (!play) return;
        const box = play.closest('[data-dw-external]');
        if (!box || box.querySelector('iframe')) return;
        const iframe = document.createElement('iframe');
        iframe.src = box.dataset.src;
        iframe.title = box.dataset.title || '';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        box.appendChild(iframe);
        play.remove();
        iframe.focus();
      });
    }

    goToMedia(mediaId) {
      if (!this.gallery) return;
      const index = this.gallery.slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
      if (index > -1) this.gallery.go(index);
    }

    /* ----------------------------------------------------------- dialogs */
    initDialogs() {
      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-dw-dialog-open]');
        if (opener) {
          const dialog = document.getElementById(opener.dataset.dwDialogOpen);
          if (dialog && typeof dialog.showModal === 'function') {
            this.lastOpener = opener;
            dialog.showModal();
          }
          return;
        }
        const closer = event.target.closest('[data-dw-dialog-close]');
        if (closer) closer.closest('dialog')?.close();
      });
      qsa(this, 'dialog[data-dw-dialog]').forEach((dialog) => {
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => {
          this.lastOpener?.focus();
          this.renderSticky();
        });
      });
    }

    /* -------------------------------------------------------- sticky bar */
    initSticky() {
      const bar = this.els.sticky;
      if (!bar || !this.data.sticky || !this.els.buy) return;
      if (document.querySelector('[data-dw-sticky-owner]')) return;
      this.setAttribute('data-dw-sticky-owner', '');
      this.stickyMq = window.matchMedia('(max-width: 989px)');
      this.sticky = { above: false, footer: false };

      const buyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.sticky.above = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        });
        this.renderSticky();
      });
      buyObserver.observe(this.els.buy);
      this.observers.push(buyObserver);

      const footer = document.querySelector('.dw-footer');
      if (footer) {
        const footerObserver = new IntersectionObserver((entries) => {
          this.sticky.footer = entries.some((entry) => entry.isIntersecting);
          this.renderSticky();
        });
        footerObserver.observe(footer);
        this.observers.push(footerObserver);
      }

      // Drawers and Dawn modals lock scrolling with a body class: hide the bar then.
      const bodyObserver = new MutationObserver(() => {
        this.syncStickyOffset();
        this.renderSticky();
      });
      bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
      this.observers.push(bodyObserver);
      this.stickyMq.addEventListener?.('change', () => this.renderSticky());
      window.addEventListener('resize', () => this.syncStickyOffset(), { passive: true });
      this.syncStickyOffset();

      bar.querySelector('[data-dw-sticky-btn]')?.addEventListener('click', () => this.addToCart());
    }

    // Keep the bar above Shopify's cookie banner when it is showing.
    syncStickyOffset() {
      const bar = this.els.sticky;
      if (!bar) return;
      const banner = document.getElementById('shopify-pc__banner');
      let offset = 0;
      if (banner) {
        const rect = banner.getBoundingClientRect();
        const style = getComputedStyle(banner);
        if (style.display !== 'none' && style.visibility !== 'hidden' && rect.height > 0 && rect.bottom >= window.innerHeight - 2) {
          offset = Math.round(rect.height);
        }
      }
      bar.style.setProperty('--dw-sticky-offset', `${offset}px`);
    }

    renderSticky() {
      const bar = this.els.sticky;
      if (!bar || !this.sticky) return;
      const blocked =
        document.body.classList.contains('overflow-hidden') ||
        document.body.classList.contains('overflow-hidden-mobile') ||
        document.body.classList.contains('overflow-hidden-tablet') ||
        Boolean(document.querySelector('dialog[open]'));
      const show = this.stickyMq.matches && this.sticky.above && !this.sticky.footer && !blocked;
      if (show === this.stickyShown) return;
      this.stickyShown = show;
      clearTimeout(this.stickyTimer);
      document.documentElement.classList.toggle('dw-sticky-on', show);
      if (show) {
        bar.hidden = false;
        requestAnimationFrame(() => bar.classList.add('is-visible'));
      } else {
        bar.classList.remove('is-visible');
        this.stickyTimer = setTimeout(() => {
          if (!this.stickyShown) bar.hidden = true;
        }, reduced() ? 0 : 260);
      }
    }

    updateSticky(totals, complete, blocked) {
      const bar = this.els.sticky;
      if (!bar) return;
      const meta = bar.querySelector('[data-dw-sticky-meta]');
      const save = bar.querySelector('[data-dw-sticky-save]');
      const button = bar.querySelector('[data-dw-sticky-btn]');
      const count = totals.count;
      if (meta) meta.textContent = count > 1 ? fill(this.strings.forPrice, { count, price: this.money(totals.total) }) : this.money(totals.total);
      if (save) {
        // The sticky bar carries the saving too, so the value of the chosen
        // set stays visible once the buy box has scrolled away.
        save.hidden = !(totals.discount > 0);
        save.textContent = fill(this.strings.save, { amount: this.money(totals.discount) });
      }
      if (button) {
        button.disabled = blocked;
        if (blocked) button.textContent = this.strings.soldOut;
        else if (!complete) button.textContent = this.strings.chooseOptions;
        else button.textContent = count > 1 ? fill(this.strings.addCount, { count }) : this.strings.addToCart;
      }
    }
  }

  customElements.define('dw-spotlight', DwSpotlight);
})();
